"use client";

import Input from "../common/Input";
import { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "../common/Button";
import ContactUsComunication from "./ContactUsComunication";
import { prepareContactUsData } from "@/helpers/partServices/contactUsForm/formatContactData";
import SelectInput from "./SelectInput";
import { usePathname } from "next/navigation";
import BouncerLoader from "../common/BouncerLoader";
import { InternalAPI } from "@/services/internalAPI/internalAPI";


const ContactUsForm = () => {
  const paths = usePathname();
  const fullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${paths}`
      : '';
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      from: fullUrl,
    }));
  }, [fullUrl]);

  const initialFormData = {
    name: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    idState: "",
    idCity: "",
    from: fullUrl,
    communicationCheckbox: "",
    reCaptchaToken: null as string | null,
    isLoading: false,
    responseMessage: "",
    showMessage: false,
  };

  const [formData, setFormData] = useState(initialFormData);

  // Objeto para almacenar los mensajes de error
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const messageTimer = () => {
    setTimeout(() => {
      setFormData((prevData) => ({
        ...prevData,
        showMessage: false,
      }));
    }, 5000);
  };

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Función para manejar el cambio en inputs y selects
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    stateSelected: null | true
  ) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "phone") {
      newValue = newValue.replace(/\D/g, "").slice(0, 10);
    }

    if (e.target instanceof HTMLSelectElement) {
      const selectedText = e.target.options[e.target.selectedIndex].text;
      setFormData((prev) => ({
        ...prev,
        [name]: selectedText,
        ...(stateSelected ? { idState: newValue } : { idCity: newValue }),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  // Función para capturar el cambio del reCAPTCHA
  const handleRecaptchaChange = (token: string | null) => {
    setFormData((prevData) => ({
      ...prevData,
      reCaptchaToken: token,
    }));
  };

  // Función de validación manual
  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "El apellido es requerido";
    }
    if (!formData.company.trim()) {
      newErrors.company = "La empresa es requerida";
    }
    if (!formData.email.trim()) {
      newErrors.email = "El correo es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "El correo no es válido";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = "El teléfono debe contener solo números";
    }
    if (!formData.idState.trim()) {
      newErrors.state = "El estado es requerido";
    }
    if (!formData.idCity.trim()) {
      newErrors.city = "La ciudad es requerida";
    }
    if (!formData.reCaptchaToken) {
      newErrors.reCaptchaToken = "Por favor, verifica que no eres un robot";
    }
    return newErrors;
  };

  const showMessage = (message: string) => {
    setFormData((prevData) => ({
      ...prevData,
      responseMessage: message,
      showMessage: true,
    }));
    messageTimer();
  }


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormData((prevData) => ({
      ...prevData,
      isLoading: true,
    }));
    // Validamos los campos
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setFormData((prevData) => ({
        ...prevData,
        isLoading: false,
      }));
      return;
    }

    if (!formData.reCaptchaToken) {
      setFormData((prevData) => ({
        ...prevData,
        isLoading: false,
      }));
      return;
    }

    try {
      const formattedData = prepareContactUsData(formData);

      const formDataToSend = new FormData();
      formDataToSend.append("body", JSON.stringify(formattedData.body));
      formDataToSend.append("reCaptchaToken", formattedData.reCaptchaToken);
      formDataToSend.append("typeForm", formattedData.typeForm);

      const formResponse = await InternalAPI.forms.sendForm(formDataToSend) as { response: string };

      showMessage(formResponse.response);

      recaptchaRef.current?.reset();
      setErrors({});
      setFormData({
        ...initialFormData,
        responseMessage: formResponse.response,
        showMessage: true,
      });
    }
    catch (err) {
      console.error(err);
      showMessage("Ocurrió un error al enviar el formulario");

      recaptchaRef.current?.reset();
      setErrors({});
      setFormData({
        ...initialFormData,
        responseMessage: "Ocurrió un error al enviar el formulario",
        showMessage: true,
      });
    }
  };

  return (
    <form className="mt-8" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="row">
          <Input
            label="Nombre*"
            name="name"
            value={formData.name}
            onChange={(e) => handleChange(e, null)}
            className="bg-white border-gray02 border w-full h-12 p-2"
          />
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
        </div>
        <div>
          <Input
            label="Apellidos*"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => handleChange(e, null)}
            className="bg-white border-gray02 border w-full h-12 p-2"
          />
          {errors.lastName && (
            <p className="text-red-500 text-xs">{errors.lastName}</p>
          )}
        </div>
      </div>
      <div>
        <Input
          label="Empresa*"
          name="company"
          value={formData.company}
          onChange={(e) => handleChange(e, null)}
          className="bg-white border-gray02 border w-full h-12 p-2"
        />
        {errors.company && (
          <p className="text-red-500 text-xs">{errors.company}</p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Input
            label="Correo*"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange(e, null)}
            className="bg-white border-gray02 border w-full h-12 p-2"
          />
          {errors.email && (
            <p className="text-red-500 text-xs">{errors.email}</p>
          )}
        </div>
        <div>
          <Input
            label="Número de teléfono*"
            name="phone"
            type="tel"
            inputMode="numeric"
            pattern="\d*"
            maxLength={10}
            value={formData.phone}
            onChange={(e) => handleChange(e, null)}
            className="bg-white border-gray02 border w-full h-12 p-2"
          />
          {errors.phone && (
            <p className="text-red-500 text-xs">{errors.phone}</p>
          )}
        </div>
        <div>
          <SelectInput
            label="Estado*"
            name="state"
            value={formData.idState}
            onChange={(e) => handleChange(e, true)}
            className="bg-white border-gray02 border w-full h-12 p-2"
          />
          {errors.state && (
            <p className="text-red-500 text-xs">{errors.state}</p>
          )}
        </div>
        <div>
          <SelectInput
            label="Ciudad*"
            name="city"
            value={formData.idCity}
            onChange={(e) => handleChange(e, null)}
            className="bg-white border-gray02 border w-full h-12 p-2"
            slug={formData.idState}
          />
          {errors.city && <p className="text-red-500 text-xs">{errors.city}</p>}
        </div>
      </div>

      <ContactUsComunication
        text=""
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 items-center mt-8">
        {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
          <div className="flex justify-center lg:justify-start lg:items-start flex-col items-center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
            />
            {errors.reCaptchaToken && (
              <p className="text-red-500 text-xs mt-2">
                {errors.reCaptchaToken}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-center lg:justify-end w-full">
          <div className="flex flex-col items-center w-72 ">
            <Button
              type="submit"
              disabled={formData.isLoading}
              className={
                formData.isLoading
                  ? "text-dark02 w-full  justify-center uppercase font-bold text-xl border-2 border-matco"
                  : "bg-matco text-dark02 w-full  justify-center border-2 uppercase font-bold text-xl hover:border-matco hover:text-matco hover:bg-transparent"
              }
            >
              {formData.isLoading ? <BouncerLoader /> : "Enviar"}
            </Button>
          </div>
        </div>
      </div>
      {formData.showMessage && (
        <div className="bg-slate-300 p-4 text-center rounded-lg mt-4 w-full">
          {formData.responseMessage}
        </div>
      )}
    </form>
  );
};

export default ContactUsForm;